import { useCallback, useEffect, useRef, useState } from 'react';
import { getClip } from '../db';
import { useAudioUnlock } from './audioUnlock';

export interface ClipPlayerState {
  /** Currently playing. */
  isPlaying: boolean;
  /** Fetching/decoding the clip blob from IndexedDB. */
  isLoading: boolean;
  /** Human-readable failure, if any. */
  error: string | null;
  /** Clip length in seconds once metadata is known (0 before that). */
  durationSec: number;
  /** Play from the start. Safe to call repeatedly (acts as replay). */
  play: () => Promise<void>;
  /** Alias of {@link play}, for the Replay button. */
  replay: () => Promise<void>;
  /** Stop and rewind. */
  stop: () => void;
  /** The underlying element, if a page needs finer control. */
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

export interface UseClipPlayerOptions {
  /** Play as soon as a new clip finishes loading. Default false. */
  autoPlay?: boolean;
  /** Fired when playback reaches the end — used to auto-advance in Verify. */
  onEnded?: () => void;
}

/**
 * Plays the stored WAV clip for one segment.
 *
 * Loads the blob from the `clips` store, wraps it in an object URL and drives a
 * single hidden <audio> element. The URL is revoked whenever the segment
 * changes or the component unmounts, so blobs don't leak across a long verify
 * session. Pass `null` to release everything.
 */
export function useClipPlayer(segmentId: string | null, options: UseClipPlayerOptions = {}): ClipPlayerState {
  const { autoPlay = false, onEnded } = options;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const unlockedRef = useRef(false);
  /** True while the silent unlock play() is in flight. See the unlock effect. */
  const unlockingRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [durationSec, setDurationSec] = useState(0);

  // one <audio> element for the lifetime of the hook
  if (audioRef.current === null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'auto';
  }

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    // The silent unlock drives the same element, so its play/metadata/error
    // events must not be mistaken for the current clip's.
    const handlePlay = () => {
      if (!unlockingRef.current) setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      if (!unlockingRef.current) onEndedRef.current?.();
    };
    const handleMeta = () => {
      if (unlockingRef.current) return;
      setDurationSec(Number.isFinite(el.duration) ? el.duration : 0);
    };
    const handleError = () => {
      if (unlockingRef.current) return;
      // `removeAttribute('src') + load()` also fires `error` on some engines;
      // that teardown is not a clip failure.
      if (!el.getAttribute('src')) return;
      setError('Could not play this clip.');
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('loadedmetadata', handleMeta);
    el.addEventListener('error', handleError);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('loadedmetadata', handleMeta);
      el.removeEventListener('error', handleError);
    };
  }, []);

  // Bless this element on the first gesture, so the auto-play of every later
  // clip (started from an IndexedDB continuation, not a gesture) is allowed.
  useAudioUnlock(audioRef, unlockedRef, unlockingRef);

  // load the blob whenever the segment changes
  useEffect(() => {
    let cancelled = false;
    const el = audioRef.current;

    /**
     * Detach the element from the current object URL *before* revoking it.
     * Revoking a URL that is still the element's `src` leaves a dead source
     * behind: any `play()` that lands in the gap fires an `error` event and the
     * card shows "Could not play this clip" for a clip that is perfectly fine.
     */
    const release = () => {
      const node = audioRef.current;
      if (node && urlRef.current && node.getAttribute('src') === urlRef.current) {
        node.removeAttribute('src');
        node.load();
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    if (!segmentId || !el) {
      el?.pause();
      release();
      setIsPlaying(false);
      setDurationSec(0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setDurationSec(0);

    getClip(segmentId)
      .then((blob) => {
        if (cancelled) return;
        if (!blob) {
          setError('No clip stored for this segment.');
          setIsLoading(false);
          return;
        }
        release();
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        el.src = url;
        setIsLoading(false);
        if (autoPlay) {
          // a clip load supersedes any in-flight silent unlock
          unlockingRef.current = false;
          el.muted = false;
          void el.play().catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load clip.');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      el.pause();
      release();
    };
  }, [segmentId, autoPlay]);

  // stop playback and revoke on unmount
  useEffect(
    () => () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    },
    [],
  );

  const play = useCallback(async () => {
    const el = audioRef.current;
    if (!el || !el.getAttribute('src')) return;
    // A tap on Play is a user gesture; from here on the element is blessed, and
    // the silent unlock (which may be mid-flight) must not stop what we start.
    unlockedRef.current = true;
    unlockingRef.current = false;
    el.muted = false;
    // Rewinding must never be able to swallow the play() call: Safari throws
    // InvalidStateError when currentTime is set before metadata has loaded, and
    // a shared try block would then skip playback entirely.
    try {
      el.currentTime = 0;
    } catch {
      /* pre-metadata seek — playback still starts from 0 */
    }
    try {
      await el.play();
    } catch {
      // autoplay policies can reject before the first user gesture — not fatal
      setIsPlaying(false);
    }
  }, []);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    try {
      el.currentTime = 0;
    } catch {
      /* nothing loaded yet */
    }
    setIsPlaying(false);
  }, []);

  return { isPlaying, isLoading, error, durationSec, play, replay: play, stop, audioRef };
}
